import { defineStore } from 'pinia';
import {
  UPDATE_SORTBY_SELECTED,
  REPLACE_SELECTED_VALUES,
  ADD_CHECKBOX_VALUES,
  REMOVE_CHECKBOX_VALUES_BY_VALUE,
  ADD_FACET_OPTIONS,
  ADD_SORT_BY_OPTIONS,
  REMOVE_CUSTOM_CHECKBOX_VALUES,
  SET_DESELECTION_PILLS_ORDER_NAMES,
  CLEAR_ALL_SELECTED_VALUES,
  SET_CONVO_SEARCH_FILTER,
  SET_ROUTE_QUERY,
  UPDATE_FACET,
} from './mutations';
import GET_ALL_SELECTED_CRITERIA from './actions';
import getParentByChildName from '../utils/getParentByChildName';
import { DEFAULT_SELECTION_ORDER } from '@/utils/facetsHelpers';

export const useFilteringFacetsStore = defineStore('filteringFacetsStore', {
  state: () => ({
    _facets: [], // renamed from facets -> _facets to avoid name collision with getter name
    _query: {},
    _sortBy: {
      // renamed from sortBy -> _sortBy to avoid name collision with getter name
      selectedValue: null,
      sortByOptions: [],
    },
    deselectionPills: {
      orderNames: [],
    },
    convoSearchFilterOptions: {},
  }),
  getters: {
    getDeliveryFacetV2(state) {
      return state._facets?.find((facet) => facet.facetType === 'DELIVERY_AND_PICKUP_V2');
    },
    sortBy(state) {
      return state._sortBy;
    },
    query(state) {
      return state._query;
    },
    facets(state) {
      return state._facets;
    },
    facetByName: (state) => (name) => state._facets.filter((facet) => facet.name === name)[0],
    facetByPropery:
      (state) =>
      ({ property, value, deep }) => {
        let targetFacet = state._facets.find((facet) => facet[property] === value);
        /* istanbul ignore else */
        if (!targetFacet && deep) {
          targetFacet = getParentByChildName(state.facets, value);
        }

        return targetFacet;
      },
    allSelectedCriteria() {
      return {
        sortBy: this.sortBy.selectedValue,
        query: this.query,
        facets: this.facets
          .filter((facet) => facet.selectedValues.length > 0)
          .map((facet) => ({
            ...facet,
          }))
          .sort(
            (a, b) =>
              /* istanbul ignore next */
              (a.selectionOrder === undefined ? DEFAULT_SELECTION_ORDER : a.selectionOrder) -
              (b.selectionOrder === undefined ? DEFAULT_SELECTION_ORDER : b.selectionOrder),
          ),
      };
    },
    getDeselectionPillsNamesOrder(state) {
      return state.deselectionPills.orderNames;
    },
    getConvoSearchFilterOptions(state) {
      return state.convoSearchFilterOptions;
    },
  },
  actions: {
    [ADD_FACET_OPTIONS]({ facets }) {
      this._facets = facets;
    },
    [ADD_SORT_BY_OPTIONS](value) {
      if (!value?.sortBy?.length) {
        this.sortBy.selectedValue = null;
        this.sortBy.sortByOptions = [];
        return;
      }
      const selectedSortOption = value.sortBy.find((element) => element.isSelected);
      if (selectedSortOption) {
        this.sortBy.selectedValue = selectedSortOption.value;
      } else {
        this.sortBy.selectedValue = value.sortBy[0].value;
      }
      this.sortBy.sortByOptions = value.sortBy;
    },
    [UPDATE_SORTBY_SELECTED](value) {
      this.sortBy.selectedValue = value;
    },
    [REPLACE_SELECTED_VALUES](criteria) {
      const facet = this.facets.filter((_facet) => _facet.name === criteria.name);
      /* istanbul ignore else */
      if (facet.length > 0) {
        facet[0].selectedValues = criteria.selectedValues;
        facet[0].selectionOrder = criteria.selectionOrder;
      } else if (criteria.selectedValues?.length) {
        // in case facet is missing and there are selected values
        this.facets.push(criteria);
      }
    },
    [CLEAR_ALL_SELECTED_VALUES]() {
      this.facets.forEach((facet) => {
        facet.selectedValues = [];
      });
    },
    [ADD_CHECKBOX_VALUES](criteria) {
      const facet = this.facets.filter((_facet) => _facet.name === criteria.name);
      /* istanbul ignore else */
      if (facet.length > 0) {
        facet[0].values.push(criteria.newValue);
      }
    },
    [REMOVE_CHECKBOX_VALUES_BY_VALUE](criteria) {
      const facet = this.facets.filter((_facet) => _facet.name === criteria.name);
      /* istanbul ignore else */
      if (facet.length > 0) {
        facet[0].values = facet[0].values.filter((value) => value.value !== criteria.value);
      }
    },
    [REMOVE_CUSTOM_CHECKBOX_VALUES]({ name, isCustomOptionFunc }) {
      const facet = this.facets.filter((_facet) => _facet.name === name);
      /* istanbul ignore else */
      if (facet.length > 0) {
        facet[0].values = facet[0].values.filter((value) => !isCustomOptionFunc(value));
      }
    },
    [SET_DESELECTION_PILLS_ORDER_NAMES]({ orderNames }) {
      this.deselectionPills.orderNames = orderNames;
    },
    [SET_CONVO_SEARCH_FILTER](payload) {
      this.convoSearchFilterOptions = { ...payload };
    },
    [SET_ROUTE_QUERY](payload = {}) {
      this._query = { ...payload };
    },
    [UPDATE_FACET](criteria = {}) {
      const facet = this.facetByPropery({
        deep: false,
        property: 'displayName',
        value: criteria.displayName,
      });
      if (facet) {
        Object.keys(criteria).forEach((key) => {
          facet[key] = criteria[key];
        });
      }
    },
    // TODO use $onAction
    [GET_ALL_SELECTED_CRITERIA]() {
      // Use for consumer to subscribe this action
    },
  },
});

export default useFilteringFacetsStore;
